import { useEffect, useState } from "react"

import { useTestAIProvider, useUpdateAIProvider } from "@/api/useTenant"
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  Modal,
  Password,
  Select,
  TextArea
} from "@/atoms"
import { getClientErrorMessage } from "@/helpers/error.helper"
import { useTranslation } from "@/hooks/useTranslation"
import { FooterModal } from "@/molecules/FooterModal"
import {
  AGENT_PLATFORM_REGIONS,
  AI_VENDORS,
  getAIProviderFormValues,
  getValidationText,
  hasStoredSecret,
  isValidJson,
  toAIProviderInput
} from "@/pages/SettingsPage/helpers/aiProvider"

import styles from "./AIProviderModal.module.scss"

import type { AIValidation, AIVendor } from "@/api/useTenant.types"
import type { AIProviderFormValues } from "@/pages/SettingsPage/helpers/aiProvider.types"
import type { AIProviderModalProps, AIProviderResult } from "."

export const AIProviderModal = ({
  provider,
  onClose
}: AIProviderModalProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "settings.kai" })
  const { t: tGeneral } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<AIProviderFormValues>()
  const initialValues = getAIProviderFormValues(provider)
  const vendor: AIVendor = Form.useWatch("vendor", form) || initialValues.vendor
  const accessKeyId: string | undefined = Form.useWatch("accessKeyId", form)
  const values = Form.useWatch([], form)
  const [isComplete, setIsComplete] = useState(false)
  const [result, setResult] = useState<AIProviderResult>()
  const tester = useTestAIProvider()
  const updater = useUpdateAIProvider()
  const keepSecret = hasStoredSecret({ vendor, accessKeyId }, provider)
  const isAgentPlatform = vendor === "gcp_agent_platform"
  const isRunning = tester.isPending || updater.isPending
  const requiredMessage = tGeneral("error.requiredField")
  const requiredText = [
    { required: true, whitespace: true, message: requiredMessage }
  ]
  const secretRules = [
    ...(keepSecret ? [] : requiredText),
    ...(isAgentPlatform
      ? [
          {
            validator: (_: unknown, value?: string) =>
              !value?.trim() || isValidJson(value)
                ? Promise.resolve()
                : Promise.reject(new Error(t("aiProvider.modal.invalidJson")))
          }
        ]
      : [])
  ]
  const secretPlaceholder = keepSecret
    ? t("aiProvider.modal.keepSecretPlaceholder")
    : undefined

  useEffect(() => {
    let isLatest = true

    form.validateFields({ validateOnly: true }).then(
      () => isLatest && setIsComplete(true),
      () => isLatest && setIsComplete(false)
    )

    return () => {
      isLatest = false
    }
  }, [form, values])

  const showValidation = (validation: AIValidation) =>
    setResult(
      validation.status === "valid"
        ? { type: "success", title: t("aiProvider.modal.valid"), detail: "" }
        : { type: "error", ...getValidationText(validation, t) }
    )

  const getCurrentInput = () =>
    toAIProviderInput({
      ...form.getFieldsValue(),
      vendor: form.getFieldValue("vendor")
    })

  const run = async (save: boolean) => {
    let values: AIProviderFormValues

    try {
      values = await form.validateFields()
    } catch {
      return
    }

    const input = toAIProviderInput({ ...values, vendor })
    // The answer is about the values sent; drop it if the form changed meanwhile.
    const isCurrent = () =>
      JSON.stringify(getCurrentInput()) === JSON.stringify(input)

    setResult(undefined)

    try {
      if (!save) {
        const { data } = await tester.mutateAsync(input)

        if (isCurrent()) showValidation(data.validation)

        return
      }

      const { data } = await updater.mutateAsync(input)

      if (!isCurrent()) return
      if (!data.saved) {
        showValidation(data.validation)

        return
      }

      message.success(t("aiProvider.modal.saved"))
      onClose()
    } catch (error: unknown) {
      if (!isCurrent()) return

      setResult({
        type: "error",
        title: t("aiProvider.modal.requestFailed"),
        detail: getClientErrorMessage(error) ?? ""
      })
    }
  }

  return (
    <Modal
      open
      width={650}
      title={t("aiProvider.modal.title")}
      closable={!isRunning}
      keyboard={!isRunning}
      mask={{ closable: !isRunning }}
      onCancel={isRunning ? undefined : onClose}
      footer={FooterModal(
        <>
          <Button disabled={isRunning} onClick={onClose}>
            {tGeneral("general.cancel")}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={updater.isPending}
            disabled={tester.isPending || !isComplete}
            onClick={() => run(true)}
          >
            {t("aiProvider.modal.testAndSave")}
          </Button>
        </>,
        {
          Aside: (
            <Button
              loading={tester.isPending}
              disabled={updater.isPending || !isComplete}
              onClick={() => run(false)}
            >
              {t("aiProvider.modal.test")}
            </Button>
          )
        }
      )}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={initialValues}
        onValuesChange={() => setResult(undefined)}
      >
        <Form.Item label={t("aiProvider.modal.vendor")} name="vendor">
          <Select<AIVendor>
            options={AI_VENDORS.map((value) => ({
              value,
              label: t(`aiProvider.vendors.${value}`)
            }))}
            onChange={(value) =>
              form.setFieldsValue({
                ...getAIProviderFormValues(provider, value),
                secret: undefined
              })
            }
          />
        </Form.Item>
        {isAgentPlatform && (
          <Form.Item
            label={t("aiProvider.modal.projectId")}
            name="projectId"
            rules={requiredText}
          >
            <Input
              autoComplete="off"
              placeholder={t("aiProvider.modal.projectIdPlaceholder")}
            />
          </Form.Item>
        )}
        <Form.Item
          label={t("aiProvider.modal.region")}
          name="region"
          rules={requiredText}
          extra={isAgentPlatform ? t("aiProvider.modal.regionHelp") : undefined}
        >
          {isAgentPlatform ? (
            <Select
              disabled={AGENT_PLATFORM_REGIONS.length === 1}
              options={AGENT_PLATFORM_REGIONS.map((value) => ({
                value,
                label: value
              }))}
            />
          ) : (
            <Input
              autoComplete="off"
              placeholder={t("aiProvider.modal.regionPlaceholder")}
            />
          )}
        </Form.Item>
        {isAgentPlatform && (
          <Form.Item
            label={t("aiProvider.modal.serviceAccountKey")}
            name="secret"
            rules={secretRules}
            extra={t("aiProvider.modal.serviceAccountKeyHelp")}
          >
            <TextArea
              rows={6}
              autoComplete="off"
              spellCheck={false}
              className={styles.secret}
              placeholder={
                secretPlaceholder ||
                t("aiProvider.modal.serviceAccountKeyPlaceholder")
              }
            />
          </Form.Item>
        )}
        {!isAgentPlatform && (
          <>
            <Form.Item
              label={t("aiProvider.modal.accessKeyId")}
              name="accessKeyId"
              rules={requiredText}
            >
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item
              label={t("aiProvider.modal.secretAccessKey")}
              name="secret"
              dependencies={["accessKeyId"]}
              rules={secretRules}
            >
              <Password
                autoComplete="new-password"
                placeholder={secretPlaceholder}
              />
            </Form.Item>
          </>
        )}
      </Form>
      {result && (
        <Alert
          showIcon
          type={result.type}
          title={result.title}
          description={result.detail || undefined}
        />
      )}
    </Modal>
  )
}
